import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Seo } from "./components/Seo";
import { Container, SectionHeader, PageHero, FinalCta, CtaButton, Icon, useReveal } from "./components/primitives";
import { ServiceCard, ServiceModal, ProcessTimeline, CaseStudyCard } from "./components/blocks";
import { SERVICES, SERVICE_CATEGORIES } from "./content/services";
import { PROCESS } from "./content/site";
import { CASE_STUDIES } from "./content/caseStudies";
import { track, EVENTS } from "./lib/analytics";
import { Photo } from "./components/Photo";
import { SERVICE_PHOTOS, HERO_SETS } from "./content/photos";

function ServiceDetail({ s, flip }) {
  const cs = CASE_STUDIES.find((c) => c.category === s.caseCategory);
  return (
    <section id={s.slug} className={`py-20 lg:py-24 scroll-mt-20 ${flip ? "bg-light-blue" : "bg-white"}`} data-testid={`service-detail-${s.slug}`}>
      <Container className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 reveal">
          <div className="rounded-2xl overflow-hidden depth-shadow-lg mb-8">
            <Photo name={SERVICE_PHOTOS[s.slug]} ratio="4/3" parallax={8} particles treat="card" sizes="(max-width: 1024px) 100vw, 33vw">
              <div className="absolute left-4 bottom-4 h-12 w-12 rounded-xl bg-[#0A1628]/80 backdrop-blur text-[#00D4FF] grid place-items-center border border-white/10"><Icon name={s.icon} className="h-6 w-6" /></div>
            </Photo>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">{s.title}</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{s.short}</p>
          <div className="mt-6 flex flex-wrap gap-2">{SERVICE_CATEGORIES.map((c) => <Badge key={c} variant="outline" className="eyebrow">{c}</Badge>)}</div>
          <div className="mt-8"><CtaButton to="/contact" testId={`service-cta-${s.slug}`} label={`Consult ${s.title}`}>Book a Consultation <ArrowRight className="ml-2 h-4 w-4" /></CtaButton></div>
        </div>
        <div className="lg:col-span-8 reveal min-w-0">
          <Tabs defaultValue="sub" onValueChange={(v) => track(EVENTS.SERVICE_INTERACTION, { service: s.slug, tab: v })}>
            <TabsList className="h-auto flex flex-wrap justify-start gap-1 rounded-2xl sm:rounded-full bg-white border border-border p-1">
              <TabsTrigger value="sub" className="nav-label rounded-full px-5 h-9" data-testid={`tab-sub-${s.slug}`}>Sub-services</TabsTrigger>
              <TabsTrigger value="process" className="nav-label rounded-full px-5 h-9" data-testid={`tab-process-${s.slug}`}>Our process</TabsTrigger>
              <TabsTrigger value="case" className="nav-label rounded-full px-5 h-9" data-testid={`tab-case-${s.slug}`}>Case study</TabsTrigger>
            </TabsList>
            <TabsContent value="sub" className="mt-6 grid sm:grid-cols-2 gap-3">
              {s.sub.map((x) => <div key={x} className="bento p-4 text-sm font-medium flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-gradient-to-br from-[#1A6EFF] to-[#00D4FF]" />{x}</div>)}
            </TabsContent>
            <TabsContent value="process" className="mt-6 grid sm:grid-cols-2 gap-3">
              {PROCESS.map((p) => <div key={p.n} className="bento p-5"><div className="eyebrow text-[#1A6EFF]">{p.n} · {p.title}</div><p className="mt-2 text-sm text-muted-foreground">{p.desc}</p></div>)}
            </TabsContent>
            <TabsContent value="case" className="mt-6">{cs && <CaseStudyCard c={cs} />}</TabsContent>
          </Tabs>
        </div>
      </Container>
    </section>
  );
}

export default function Services() {
  const ref = useReveal(); const [open, setOpen] = useState(null);
  return (
    <div ref={ref}>
      <Seo title="Services" description="AI & ML, Generative AI, Data Engineering, AI Automation, Cloud & AI Infrastructure and Custom Software — delivered as advisory, implementation, migration or managed services." />
      <PageHero photo={HERO_SETS.services} eyebrow="Services" title="Services engineered for measurable business outcomes." desc="Six areas of expertise, one integrated team. Every service is available as Advisory, Implementation, Migration or Managed Services.">
        <CtaButton to="/contact" testId="services-hero-cta">Book a Consultation <ArrowRight className="ml-2 h-4 w-4" /></CtaButton>
        <CtaButton to="/case-studies" variant="outline">See case studies</CtaButton>
      </PageHero>
      <nav aria-label="Service navigation" className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-border">
        <Container className="flex gap-1 overflow-x-auto py-2 no-scrollbar">
          {SERVICES.map((s) => <a key={s.slug} href={`#${s.slug}`} data-testid={`service-nav-${s.slug}`} className="nav-label whitespace-nowrap px-4 h-10 inline-flex items-center rounded-full hover:bg-[#F0F4FF] hover:text-[#1A6EFF] transition-colors">{s.title}</a>)}
        </Container>
      </nav>
      <section className="py-20 bg-light">
        <Container>
          <SectionHeader eyebrow="Overview" title="Choose a service to explore sub-services, process and a case study." className="reveal" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">{SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} index={i} onOpen={setOpen} />)}</div>
        </Container>
      </section>
      {SERVICES.map((s, i) => <ServiceDetail key={s.slug} s={s} flip={i % 2 === 1} />)}
      <section className="py-24 bg-navy text-white"><Container><SectionHeader dark eyebrow="Delivery model" title="How every engagement runs." className="reveal" /><div className="mt-14"><ProcessTimeline dark /></div></Container></section>
      <section className="py-24 bg-light"><Container><div className="flex items-end justify-between gap-6 reveal"><SectionHeader eyebrow="Case studies" title="Related outcomes." /><Link to="/case-studies" className="text-sm font-semibold text-[#1A6EFF] inline-flex items-center gap-1">All case studies <ArrowRight className="h-4 w-4" /></Link></div><div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">{CASE_STUDIES.slice(0, 3).map((c, i) => <CaseStudyCard key={c.id} c={c} index={i} />)}</div></Container></section>
      <FinalCta />
      <ServiceModal service={open} onClose={() => setOpen(null)} />
    </div>
  );
}
